import { useEffect } from 'react'
import { isClient, isServer } from './nextJsUtils'

/**
 * Will auto focus the element the first time it renders.
 * The element must be focusable, such as an input or have tabIndex="0".
 *
 * @example
 * // Here the error message will become focused when it's shown
 * <label ref={ autoFocusRef() } tabIndex="0">
 *   Error
 * </label>
 *
 * @param [ref] {Function} You can pass your own ref as a param if you need to do additional things with the ref element, e.g.
 *         const divRef = useRef()
 *         <div ref={autoFocusRef(divRef)} />
 *
 * @returns {Function} A ref function that receives the ref element
 */
export const autoFocusRef = ref => el => {
  if (el && el.dataset && !el.dataset.autoFocused) {
    // eslint-disable-next-line no-param-reassign
    el.dataset.autoFocused = true
    el.focus()
  }
  if (typeof ref === 'function') {
    ref(el)
  }
}

export const isInViewport = elem => {
  if (!elem) return false
  const bounding = elem.getBoundingClientRect()
  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

export const getElIndexInParent = el => (el ? Array.from(el?.parentElement?.children ?? []).indexOf(el) : undefined)

export const dataAttr = condition => (condition ? '' : undefined)

export const ariaAttr = condition => (condition ? true : undefined)

/**
 * Get an element's content's width (just contents, no padding, border, etc)
 * @param element
 * @returns {number}
 */
export function getContentWidth(element, fallback = 0) {
  if (isServer() || !element) return fallback
  const styles = window.getComputedStyle(element)
  return element.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight)
}

export function preloadImage(src) {
  if (src && isClient()) new window.Image().src = src
}

export const InputModalityInit = () => {
  useAddInputModalityBodyAttribute()
  return null
}

/**
 * A Hook that adds a data-using-mouse or data-using-keyboard attribute to the body element, depending
 * on the user's current input modality. Great for styling based on input modality.
 * For example, you might only have a focus outline while using keyboard.
 *
 * @example
 * // CSS to change bg based on input modality
 * <Box
 *   sx={{
 *     '[data-using-mouse] &': {
 *       bg: 'blue.100'
 *     },
 *     '[data-using-keyboard] &': {
 *       bg: 'red.100'
 *     }
 *   }}
 * >
 *
 */
export function useAddInputModalityBodyAttribute() {
  useEffect(() => {
    function onMouseMove(e) {
      // Native <select> elements fire synthetic mousemove events when the
      // dropdown closes (e.g. after pressing Enter to pick an option).
      // Ignore these zero-movement events so we don't falsely switch away
      // from keyboard modality.
      if (e.movementX === 0 && e.movementY === 0) {
        return
      }
      delete document.body.dataset.usingKeyboard
      document.body.dataset.usingMouse = 'true'
      clearAll()
      document.addEventListener('keydown', onKeyDown)
    }

    function onKeyDown(e) {
      const ARROW_KEYS = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown']
      const NAVIGATION_KEYS = ['Tab', ...ARROW_KEYS]
      if (!NAVIGATION_KEYS.includes(e.key)) {
        return
      }
      // If we are within an input, textarea, or contenteditable, don't switch to keyboard modality
      const isRegularInputElement =
        (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName) || document.activeElement.isContentEditable) &&
        // If it has a role it's possibly acting as a special component so we'll switch to keyboard on arrow keys
        document.activeElement.getAttribute('role') === null

      if (ARROW_KEYS.includes(e.key) && isRegularInputElement) {
        return
      }
      delete document.body.dataset.usingMouse
      document.body.dataset.usingKeyboard = 'true'
      clearAll()
      document.addEventListener('mousemove', onMouseMove)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('keydown', onKeyDown)

    function clearAll() {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('keydown', onKeyDown)
    }

    return clearAll
  }, [])
}

/**
 * A util that, provided a dom element, an attr name, and a callback, will call the callback everytime that
 * attribute on the element changes. The callback receives the new value.
 *
 * You can also pass a parent element and listen for that attribute to change on all nested nodes within, just pass
 * { subtree: true } as the fourth param. In this case you'll receive the target element, who's attribute changed
 * as the second param of your callback.
 *
 * @param el {HTMLElement}
 * @param attrName {string}
 * @param cb {(value: unknown, targetEl: HTMLElement, mutation: MutationRecord) => void}
 * @param opts {Object}
 * @param opts.subtree {true}
 *
 * @returns {function(): void}
 */
export function onAttributeChange(el, attrName, cb, opts = {}) {
  const mutationCallback = mutationsList => {
    mutationsList.forEach(m => {
      if (m.type === 'attributes' && m.attributeName === attrName) {
        cb(m.target.getAttribute(attrName), m.target, m)
      }
    })
  }
  const observer = new window.MutationObserver(mutationCallback)
  observer.observe(el, { attributes: true, subtree: opts.subtree })
  return () => observer.disconnect()
}

const TEXT_NODE = 3
const ELEMENT_NODE = 1

/**
 * Get the text content of a node, with a custom delimiter between elements.
 * Similar to node.textContent, but allows a custom delimiter between elements
 * so that the text doesn't get smashed together.
 * @param node
 * @param [delimiter=' '] {string} The delimiter to use between elements. Default is a space.
 * @returns {string}
 */
export function getTextContentWithDelimiter(node, delimiter = ' ') {
  if (!node) return ''
  if (node.nodeType === TEXT_NODE) {
    return node.textContent ?? ''
  }
  if (node.nodeType === ELEMENT_NODE) {
    const children = Array.from(node.childNodes)
    return children
      .map(child => getTextContentWithDelimiter(child, delimiter))
      .filter(text => text !== '')
      .join(delimiter)
      .replace(/\s+/g, ' ') // collapse multiple whitespace into a single delimiter
      .trim()
  }
  return ''
}

/**
 * Wait for an element to exist in the DOM and call a function when it does.
 *
 * @param selector {string} A CSS selector to find the element
 * @param onExists {function} A function to call when the element exists
 * @param timeoutOpts {object} An object with the following options:
 * @param timeoutOpts.timeout {number} The number of milliseconds to wait before giving up, default is 5000
 * @param timeoutOpts.onTimeout {function} A function to call when the timeout is reached
 *
 * @example
 * waitForElement('.my-element', function onExists(element) {
 *   console.log('Element exists:', element)
 * })
 */
export function waitForElement(selector, onExists, timeoutOpts = undefined) {
  function condition() {
    return document.querySelector(selector)
  }
  waitUntil(condition, onExists, timeoutOpts)
}

/**
 * Wait for a condition fn to return truthy and call a function when it does.
 *
 * @param condition {() => boolean} A function that returns a boolean, when it returns true, the onSuccess function is called
 * @param onSuccess {function} A function to call when the element exists
 * @param timeoutOpts {object} An object with the following options:
 * @param timeoutOpts.timeout {number} The number of milliseconds to wait before giving up, default is 5000
 * @param timeoutOpts.onTimeout {function} A function to call when the timeout is reached
 *
 * @example
 * waitUntil(() => window.ReactJobForm, function onSuccess() {
 *   console.log('Now safe to access window.ReactJobForm')
 *   window.ReactJobForm.getFormContext().foo
 * })
 */
export function waitUntil(condition, onSuccess, { timeout = 5000, interval = 25, onTimeout } = {}) {
  domReady(function () {
    const startTime = performance.now()
    const conditionResult = condition()
    if (conditionResult) {
      onSuccess(conditionResult)
      return
    }

    const intervalId = setInterval(() => {
      if (performance.now() - startTime >= timeout) {
        clearInterval(intervalId)
        if (typeof onTimeout === 'function') onTimeout()
      } else if (condition()) {
        clearInterval(intervalId)
        onSuccess(condition())
      }
    }, interval)
  })
}

export function domReady(fn) {
  let didRun = false

  function run() {
    if (didRun || !document.body) return
    didRun = true
    document.removeEventListener('DOMContentLoaded', run)
    fn()
  }

  document.addEventListener('DOMContentLoaded', run)
  run()
}
